import {
    DraasApiSiteExtended,
    DraasApiAwsSite,
    DraasApiVSphereSite,
    DraasApiFlashArray,
    DraasApiSiteRecoverySummary,
    DraasApiSiteProtectionSummary,
    DraasApiRecoveryJobExecutionsSummary,
    DraasApiIsSiteDeletable,
} from '@pure/paas-api-gateway-client-ts';
import moment from 'moment';

export class DisasterRecoverySiteExtended {
    site: DisasterRecoveryAwsSite | DisasterRecoveryVSphereSite;
    recoverySummary: DisasterRecoverySiteRecoverySummary;
    protectionSummary: DisasterRecoverySiteProtectionSummary;

    constructor(json: DraasApiSiteExtended) {
        this.site =
            json.site.type === 'AWS'
                ? new DisasterRecoveryAwsSite(json.site)
                : new DisasterRecoveryVSphereSite(json.site as DraasApiVSphereSite);
        this.recoverySummary = new DisasterRecoverySiteRecoverySummary(json.recovery_summary);
        this.protectionSummary = new DisasterRecoverySiteProtectionSummary(json.protection_summary);
    }
}

export class DisasterRecoverySite {
    status: DisasterRecoverySiteStatus;
    siteType: DisasterRecoverySiteType;
    name: string;
    id: string;
    version: number;
    clusterId: string;
    lastUpdatedAtUtc?: string;

    constructor(json: DraasApiAwsSite | DraasApiVSphereSite) {
        this.status = json.status;
        this.siteType = json.site_type;
        this.name = json.name;
        this.id = json.id;
        this.version = json.version;
        this.clusterId = json.cluster_id;
        this.lastUpdatedAtUtc = json.last_updated_at_utc;
    }
}

export type DisasterRecoverySiteType = 'VSPHERE' | 'AWS';

export type DisasterRecoverySiteStatus =
    | 'HEALTHY'
    | 'UNHEALTHY'
    | 'PENDING_DEPLOYMENT'
    | 'NOT_STARTED'
    | 'CREDENTIALS_INVALID'
    | 'DELETION_IN_PROGRESS'
    | 'UNKNOWN';

export class DisasterRecoveryAwsSite extends DisasterRecoverySite {
    instanceType: string;
    region: string;

    constructor(json: DraasApiAwsSite) {
        super(json);
        this.instanceType = json.instance_type;
        this.region = json.region;
    }
}

export class DisasterRecoveryFlashArray {
    id: string;
    hostname: string;

    constructor(json: DraasApiFlashArray) {
        this.id = json.id;
        this.hostname = json.hostname;
    }
}

export class DisasterRecoveryVSphereSite extends DisasterRecoverySite {
    storageQuotaInBytes?: number;
    flashArrays: DisasterRecoveryFlashArray[];

    constructor(json: DraasApiVSphereSite) {
        super(json);
        this.storageQuotaInBytes = json.storage_quota_in_bytes;
        this.flashArrays =
            json.flash_arrays?.map(jsonFlashArray => new DisasterRecoveryFlashArray(jsonFlashArray)) ?? [];
    }
}

export class DisasterRecoverySiteRecoverySummary {
    recoveryJobExecutionSummary: DisasterRecoveryRecoveryJobExecutionsSummary;
    lastRto: moment.Duration;

    constructor(json: DraasApiSiteRecoverySummary) {
        this.recoveryJobExecutionSummary = json.recovery_job_execution_summary
            ? new DisasterRecoveryRecoveryJobExecutionsSummary(json.recovery_job_execution_summary)
            : null;
        this.lastRto = json.last_rto_duration ? moment.duration(json.last_rto_duration) : null;
    }
}

/**
 * Class representing whether a site is deletable and if not, why
 */
export class DisasterRecoveryIsSiteDeletable {
    /** Whether the site is deletable */
    deletable: boolean;

    /** Number of the undeleted Protection Groups that are using this site as source or target and are blocking the deletion */
    numberOfGroups: number;

    /** Names of the first two undeleted Protection Groups that are using this site as source or target and are blocking the deletion */
    firstTwoGroupNames: string[];

    /** Number of the Moving Plans that are using this site as target and are blocking the deletion */
    numberOfPlans: number;

    /** Names of the first two Moving Plans that are using this site as target and are blocking the deletion */
    firstTwoPlanNames: string[];

    constructor(json: DraasApiIsSiteDeletable) {
        this.deletable = json.deletable;
        this.numberOfGroups = json.number_of_groups;
        this.firstTwoGroupNames = json.first_two_group_names;
        this.numberOfPlans = json.number_of_plans;
        this.firstTwoPlanNames = json.first_two_plan_names;
    }
}

export class DisasterRecoveryRecoveryJobExecutionsSummary {
    recovered: number;
    failed: number;
    notTested: number;
    total: number;

    constructor(json: DraasApiRecoveryJobExecutionsSummary) {
        this.recovered = json.recovered;
        this.failed = json.failed;
        this.notTested = json.not_tested;
        this.total = json.total;
    }
}

export class DisasterRecoverySiteProtectionSummary {
    protectedVms: number;
    discoveredVms: number;
    protectedBytes: number;

    constructor(json: DraasApiSiteProtectionSummary) {
        this.protectedVms = json.protected_vms;
        this.discoveredVms = json.discovered_vms;
        this.protectedBytes = json.protected_bytes;
    }
}
